import { useState, useCallback, useMemo } from 'react'
import { FileText } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { QuoteItem_, useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { Badge } from 'src/shared/components/ui/badge'
import { Input } from 'src/shared/components/ui/input'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { TableCell, TableRow } from 'src/shared/components/ui/table'
import { DeleteConfirmDialog } from '../../shared/DeleteConfirmDialog'
import { useDebouncedFieldUpdate } from '../../../hooks'
import { LineItemNotesField } from '../../shared/LineItemNotesPopover'
import { cn } from 'src/shared/components/ui/utils'
import {
  QUOTE_ITEM_ROW_STANDALONE,
  QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE,
} from '../../../consts'
import { ProductRowTotalCell } from '../ProductRow/components/cells/ProductRowTotalCell'
import {
  calculateProductRowBreakdown,
  getProductReceiptLines,
} from '../../../utils'

export const CustomRow = ({
  quote_item,
  isEditMode = false,
  showDiscountColumn = true,
  isReadOnly = false,
}: {
  quote_item: QuoteItem_
  isEditMode?: boolean
  showDiscountColumn?: boolean
  isReadOnly?: boolean
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { updateQuoteItem, deleteQuoteItem } = useQuoteItemApi({})
  const { createQuoteSubItem, deleteQuoteSubItem } = useQuoteSubItemApi()

  const updateDescription = useCallback(
    (value: string) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          description: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateUnitPrice = useCallback(
    (value: number) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          unit_price: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateQuantity = useCallback(
    (value: number) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          quantity: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateDiscount = useCallback(
    (value: number) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          discount: value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )
  const updateNotes = useCallback(
    (value: string) => {
      if (quote_item?.id && quote_item?.quote_id) {
        updateQuoteItem.mutate({
          id: quote_item.id,
          quote_id: quote_item.quote_id,
          notes: value.trim() === '' ? null : value,
        })
      }
    },
    [quote_item?.id, quote_item?.quote_id, updateQuoteItem]
  )

  const [descriptionLocal, , handleDescriptionChange] = useDebouncedFieldUpdate(
    quote_item.description || '',
    updateDescription
  )
  const [unitPriceLocal, , handleUnitPriceChange] = useDebouncedFieldUpdate(
    quote_item.unit_price || 0,
    updateUnitPrice
  )
  const [quantityLocal, , handleQuantityChange] = useDebouncedFieldUpdate(
    quote_item.quantity || 1,
    updateQuantity
  )
  const [discountLocal, , handleDiscountChange] = useDebouncedFieldUpdate(
    quote_item.discount || 0,
    updateDiscount
  )
  const [notesLocal, , handleNotesChange] = useDebouncedFieldUpdate(
    quote_item.notes ?? '',
    updateNotes
  )

  const unitPrice = isEditMode ? unitPriceLocal : quote_item.unit_price || 0
  const quantity = isEditMode ? quantityLocal : quote_item.quantity || 1
  const discount = isEditMode ? discountLocal : quote_item.discount || 0
  const total = Math.max(0, unitPrice * quantity - discount)
  const subtotalBeforeDiscount = unitPrice * quantity

  const breakdown = useMemo(
    () =>
      calculateProductRowBreakdown({
        ...quote_item,
        unit_price: unitPrice,
        quantity,
        discount,
      }),
    [quote_item, unitPrice, quantity, discount]
  )
  const receiptLines = useMemo(
    () =>
      getProductReceiptLines(quote_item, t, {
        unitPrice,
        quantity,
        discount,
        mainLabel: t('Amount', 'Belopp'),
      }),
    [quote_item, unitPrice, quantity, discount, t]
  )

  const handleAddCustomSubRow = async () => {
    await createQuoteSubItem.mutateAsync({
      quote_item_id: quote_item.id,
      type: QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE,
      description: t('New line', 'Ny rad'),
      quantity: 1,
      unit_price: 0,
    })
  }

  /** When description has a second line (notes or edit), top-align like ProductRow; otherwise center with numeric columns. */
  const hasSecondLineInDescription =
    isEditMode || !isReadOnly || !!quote_item.notes?.trim()

  return (
    <>
      <TableRow className={cn(QUOTE_ITEM_ROW_STANDALONE, 'group')}>
        <TableCell
          className={cn(
            'py-2 px-5 text-sm text-gray-700',
            hasSecondLineInDescription || isEditMode
              ? 'align-top'
              : 'align-middle'
          )}
        >
          <Row className={cn('gap-3', 'items-center')}>
            <div className="flex items-center justify-center w-14 h-14 rounded-lg p-1.5 border border-gray-200 shrink-0">
              <FileText className="text-gray-600 w-5 h-5" />
            </div>
            <Column className="min-w-0 max-w-full flex-1 gap-1.5">
              {isEditMode ? (
                <Input
                  variant="plain"
                  value={descriptionLocal}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="w-full text-sm text-foreground"
                />
              ) : (
                <span className="truncate font-normal text-gray-900 max-sm:whitespace-normal">
                  {t(quote_item.description || '')}
                </span>
              )}

              <LineItemNotesField
                value={notesLocal}
                onChange={handleNotesChange}
                canEdit={!isReadOnly}
                readOnlyNote={quote_item.notes}
              />
            </Column>
          </Row>
        </TableCell>

        {showDiscountColumn && (
          <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-center">
            {isEditMode ? (
              <Input
                type="number"
                value={discountLocal}
                onChange={(e) =>
                  handleDiscountChange(
                    Number((e.target as HTMLInputElement).value) || 0
                  )
                }
                className="w-20 h-7 text-center text-sm px-1 border-gray-300 mx-auto"
              />
            ) : discount > 0 ? (
              <Badge variant="red">{discount}%</Badge>
            ) : (
              discount.toLocaleString('sv-SE')
            )}
          </TableCell>
        )}

        <ProductRowTotalCell
          discount={discount}
          subtotalBeforeDiscount={subtotalBeforeDiscount}
          total={total}
          showDelete={!isReadOnly || isEditMode}
          onDeleteClick={() => setDeleteDialogOpen(true)}
          priceSummaryLines={receiptLines}
          lineTotal={breakdown.total}
          isEditMode={isEditMode}
          productUnitPrice={unitPrice}
          onProductUnitPriceChange={
            isEditMode ? handleUnitPriceChange : undefined
          }
          onDiscountChange={isEditMode ? handleDiscountChange : undefined}
          onDeleteSubItem={
            isEditMode ? (id) => deleteQuoteSubItem.mutate(id) : undefined
          }
          onAddCustomLine={
            isEditMode && isAdmin && !isReadOnly
              ? handleAddCustomSubRow
              : undefined
          }
          canAddCustomLine={isAdmin}
          quantity={quantity}
          onQuantityChange={handleQuantityChange}
          isReadOnly={isReadOnly}
        />
      </TableRow>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('Delete row', 'Ta bort rad')}
        description={`${t(
          'Are you sure you want to delete this row?',
          'Är du säker på att du vill ta bort denna rad?'
        )} ${t(
          'This action cannot be undone.',
          'Denna åtgärd kan inte ångras.'
        )}`}
        onConfirm={() =>
          deleteQuoteItem.mutate({
            id: quote_item.id,
            quote_id: quote_item.quote_id!,
          })
        }
      />
    </>
  )
}
